import { Link, useNavigate } from 'react-router-dom';
import { useGetInvestorDashboardQuery, kycStatusDisplay } from '../../features/dashboard/dashboardApi';
import { useListMyInvestmentsQuery } from '../../features/investments/investorInvestmentsApi';
import { usePlanLookup } from '../../components/investments/usePlanLookup';
import InvestmentStatusBadge from '../../components/investments/InvestmentStatusBadge';
import Card, { CardHeader } from '../../components/ui/Card';
import StatStrip, { StatStripItem } from '../../components/ui/StatStrip';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import CountUpStat from '../../components/ui/CountUpStat';
import { SkeletonCardGrid, SkeletonTable } from '../../components/ui/Skeleton';
import { formatCurrency } from '../../lib/formatPlan';
import { authErrorMessage } from '../../lib/authErrorMessage';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * GET /investor/dashboard — any authenticated investor, scoped
 * automatically, no account_status gate on the endpoint itself. Unlike the
 * old version of this page (which hid everything behind a "complete your
 * KYC" wall until account_status === 'active'), the real dashboard returns
 * valid (zeroed) data for any investor and surfaces kyc_status as ordinary
 * page content — a pill, not a gate — so a not-yet-approved investor still
 * sees their dashboard, just with a nudge above it and an empty
 * investments table.
 *
 * The dashboard response has no investments list of its own — "My
 * investments" here is pulled from the already-integrated
 * GET /investor/investments, per the spec's explicit instruction not to
 * invent fields on the dashboard response that aren't there.
 */
export default function InvestorDashboard() {
  const navigate = useNavigate();
  const { data: dashboard, isLoading, isError, error } = useGetInvestorDashboardQuery();
  const { data: investmentsData, isLoading: isLoadingInvestments } = useListMyInvestmentsQuery({ per_page: 5 });
  const getPlan = usePlanLookup();

  const investments = investmentsData?.investments ?? [];
  const investmentsTotal = investmentsData?.meta?.total ?? investments.length;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonCardGrid count={3} className="grid grid-cols-1 sm:grid-cols-3 gap-4" />
        <SkeletonCardGrid count={3} className="grid grid-cols-1 sm:grid-cols-3 gap-4" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card className="max-w-xl">
        <p className="text-sm text-danger">{authErrorMessage(error, { fallback: 'Could not load your dashboard.' })}</p>
      </Card>
    );
  }

  if (!dashboard) return null;

  const kyc = kycStatusDisplay(dashboard.kycStatus);
  const needsKyc = dashboard.kycStatus !== 'active' && dashboard.kycStatus !== 'approved';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">My dashboard</h1>
        <p className="text-foreground-muted text-sm mt-1">Your investments at a glance.</p>
      </div>

      {needsKyc && (
        <Card className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-muted">
            Your KYC status is <Badge tone={kyc.tone}>{kyc.label}</Badge>. You'll get full access to your portfolio once
            it's approved.
          </p>
          <Link to="/kyc">
            <Button size="sm">{dashboard.kycStatus === 'rejected' ? 'Resubmit KYC' : 'Complete KYC'}</Button>
          </Link>
        </Card>
      )}

      <StatStrip columns={3}>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Total invested</p>
          <CountUpStat
            value={Number(dashboard.totalInvested) || 0}
            formatter={(n) => formatCurrency(n)}
            className="text-2xl font-bold text-foreground mt-2 tabular-nums"
          />
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Active investments</p>
          <CountUpStat
            value={dashboard.activeInvestmentsCount}
            formatter={(n) => Math.round(n).toLocaleString()}
            className="text-2xl font-bold text-foreground mt-2 tabular-nums"
          />
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Interest earned</p>
          <CountUpStat
            value={Number(dashboard.totalInterestEarned) || 0}
            formatter={(n) => formatCurrency(n)}
            className="text-2xl font-bold text-accent mt-2 tabular-nums"
          />
        </StatStripItem>
      </StatStrip>

      <StatStrip columns={3}>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Payments made</p>
          <p className="text-xl font-semibold text-foreground mt-2">{formatCurrency(dashboard.paymentsMadeTotal)}</p>
          <p className="text-xs text-foreground-subtle mt-1">{dashboard.paymentsMadeCount} payments, YTD</p>
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">Last payout received</p>
          {dashboard.lastPayoutAmount ? (
            <>
              <p className="text-xl font-semibold text-foreground mt-2">{formatCurrency(dashboard.lastPayoutAmount)}</p>
              <p className="text-xs text-foreground-subtle mt-1">{formatDate(dashboard.lastPayoutDate)}</p>
            </>
          ) : (
            <p className="text-sm text-foreground-muted mt-2">No payouts yet</p>
          )}
        </StatStripItem>
        <StatStripItem>
          <p className="text-foreground-subtle text-sm">KYC status</p>
          <div className="mt-2.5">
            <Badge tone={kyc.tone}>{kyc.label}</Badge>
          </div>
        </StatStripItem>
      </StatStrip>

      <Card>
        <CardHeader
          title="My investments"
          action={
            investmentsTotal > investments.length && (
              <Link to="/my-investments" className="text-sm text-accent hover:underline">
                View all
              </Link>
            )
          }
        />

        {isLoadingInvestments && <SkeletonTable columns={['Plan', 'Status', 'Principal', 'Maturity']} rows={3} />}

        {!isLoadingInvestments && investments.length === 0 && (
          <p className="py-6 text-center text-foreground-muted">
            You don't have any investments yet.{' '}
            <Link to="/plans" className="text-accent hover:underline">
              Browse plans
            </Link>{' '}
            to get started.
          </p>
        )}

        {!isLoadingInvestments && investments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-foreground-subtle border-b border-border">
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Principal</th>
                  <th className="py-2 font-medium">Maturity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {investments.map((inv) => {
                  const plan = getPlan(inv.investmentPlanUuid);
                  return (
                    <tr
                      key={inv.id}
                      className="cursor-pointer hover:bg-background-elevated transition-colors"
                      onClick={() => navigate(`/my-investments/${inv.id}`)}
                    >
                      <td className="py-3 pr-4 text-foreground">{plan?.name || 'Investment'}</td>
                      <td className="py-3 pr-4">
                        <InvestmentStatusBadge status={inv.status} />
                      </td>
                      <td className="py-3 pr-4 text-foreground-muted">{formatCurrency(inv.principal)}</td>
                      <td className="py-3 text-foreground-muted">{formatDate(inv.maturityDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {dashboard.openSupportTickets > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-muted">
            {dashboard.openSupportTickets} open support ticket{dashboard.openSupportTickets === 1 ? '' : 's'}
          </p>
          <Link to="/tickets">
            <Button size="sm" variant="secondary">
              View
            </Button>
          </Link>
        </Card>
      )}
    </div>
  );
}
